import { useHttp } from "../hooks/http.hook";
import { _apiBase } from "../components/pages";

import {
  transformDiseaseToSend,
  transformDrugToUpdate,
  transformGeneTestToSend,
  transformLabTestToSend,
  transformMeasurementToSend,
  transformNewDrugToSend,
  transformNewReceivedSymptoms,
  transformNewSymptomsToSend,
  transformPatientToUpdate,
  transformReceivedDrugs,
  transformReceivedGenes,
  transformReceivedLabTests,
  transformReceivedMeasurements,
  transformReceivedSymptoms,
  transformReceivedUser,
  transformUpdatedSymptomToSend,
} from "./TransformUtils";

import { removeLastSign } from "../components/pages/utils";
import { Consts } from "../components/pages/consts";

const useMmcService = () => {
  const { loading, request, error, clearError } = useHttp();

  const getDbUser = async (bearer, userId) => {
    if (userId > 0) {
      const user = await request(`${_apiBase}user/get/${userId}`, Consts.GET, null, {
        "Content-Type": Consts.APPLICATION_JSON,
        Authorization: bearer,
      });

     return transformReceivedUser(user);
    }

    return null;
  };

  const login = async (body) => {
    return await request(`${_apiBase}login`, Consts.POST, body, {
      Accept: Consts.APPLICATION_JSON,
      "Content-Type": Consts.APPLICATION_JSON,
    });
  };

  const getDbLabTests = async (bearer, patientId) => {
    if (patientId > 0) {
      const res = await request(
        `${_apiBase}lab_test/list/${patientId}`,
        Consts.GET,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );

      return transformReceivedLabTests(res);
    }

    return null;
  };

  const deleteDbLabTest = async (bearer, labTestId) => {
    if (labTestId > 0) {
      return await request(
        `${_apiBase}lab_test/delete/${labTestId}`,
        Consts.DELETE,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const saveDbLabTest = async (bearer, labTest, patientId) => {
    if (labTest && patientId > 0) {
      const response = await request(
        `${_apiBase}lab_test/create/${patientId}`,
        Consts.POST,
        transformLabTestToSend(labTest),
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );

      labTest.id = response?.id;
      return response;
    }

    return null;
  };

  const getDbMeasurements = async (bearer, patientId) => {
    if (patientId > 0) {
      const result = await request(
        `${_apiBase}measurement/list/${patientId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return transformReceivedMeasurements(result);
    }

    return null;
  };

  const getDbWeight = async (bearer, patientId) => {
    if (patientId > 0) {
      const resultWeight = await request(
        `${_apiBase}measurement/weight/${patientId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return resultWeight?.weight;
    }

    return null;
  };

  const getDbHeight = async (bearer, patientId) => {
    if (patientId > 0) {
      const resultHeight = await request(
        `${_apiBase}measurement/height/${patientId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return resultHeight.height;
    }

    return null;
  };

  const saveDbDisease = async (bearer, disease, patientId) => {
    if (disease && patientId > 0) {
      const res = await request(
        `${_apiBase}disease/create/${patientId}`,
        Consts.POST,
        transformDiseaseToSend(disease),
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return res?.id;
    }

    return null;
  };

  const saveDbMeasurement = async (bearer, patientId, measurement) => {
    if (measurement && patientId > 0) {
      return await request(
        `${_apiBase}measurement/create/${patientId}`,
        Consts.POST,
        transformMeasurementToSend(measurement),
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );
    }

    return null;
  };

  const updateDbUser = async (bearer, patient) => {
    if (patient) {
      const result = await request(
        `${_apiBase}user/update/${patient?.id}`,
        Consts.PUT,
        transformPatientToUpdate(patient),
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return !result ? -1 : result;
    }

    return -1;
  };

  const getDbDrugs = async (bearer, patientId, risks, intakes) => {
    if (patientId > 0) {
      const result = await request(
        `${_apiBase}prescription/list/${patientId}/${risks}/${intakes}`,
        Consts.GET,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );

      return transformReceivedDrugs(result, risks, intakes);
    }

    return null;
  };

  const deleteDbDrug = async (bearer, drugId, stopDate) => {
    if (drugId > 0) {
      return await request(
        `${_apiBase}prescription/delete/${drugId}/${stopDate}`,
        Consts.DELETE,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const deleteDbMeasurement = async (bearer, measurementId) => {
    if (measurementId > 0) {
      return await request(
        `${_apiBase}measurement/delete/${measurementId}`,
        Consts.DELETE,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const deleteDbDisease = async (bearer, diseaseId) => {
    if (diseaseId > 0) {
      return await request(
        `${_apiBase}disease/delete/${diseaseId}`,
        Consts.DELETE,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );
    }

    return null;
  };

  const getDrugProductsJson = async (bearer, language) => {
    return await request(`${_apiBase}drugProducts/${language}`, Consts.GET, null, {
      Accept: Consts.APPLICATION_JSON,
      "Content-Type": Consts.APPLICATION_JSON,
      Authorization: bearer,
    });
  };

  const getDrugSubstancesJson = async (bearer, language) => {
    return await request(`${_apiBase}drugSubstances/${language}`, Consts.GET, null, {
      Accept: Consts.APPLICATION_JSON,
      "Content-Type": Consts.APPLICATION_JSON,
      Authorization: bearer,
    });
  };

  const getDrugByProductNameJson = async (bearer, productName, language) => {
    const newProductName = removeLastSign(productName, ",");
    const encodedProductName = encodeURIComponent(newProductName);

    return await request(
      `${_apiBase}drug/get/${encodedProductName}/${language}`,
      Consts.GET,
      null,
      {
        Accept: Consts.APPLICATION_JSON,
        "Content-Type": Consts.APPLICATION_JSON,
        Authorization: bearer,
      },
    );
  };

  const getDrugBySubstancesJson = async (bearer, substances, language) => {
    const encodedSubstances = encodeURIComponent(substances);

    if(encodedSubstances && language?.length === 2){
      return await request(
        `${_apiBase}drug/getAtc/${encodedSubstances}/${language}`,
        Consts.GET,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const getPromsJson = async (bearer) => {
    return await request(`${_apiBase}allSymptoms/`, Consts.GET, null, {
      Accept: Consts.APPLICATION_JSON,
      "Content-Type": Consts.APPLICATION_JSON,
      Authorization: bearer,
    });
  };

  const getDpwgJson = async (bearer) => {
    return await request(`${_apiBase}dpwg/`, Consts.GET, null, {
      Accept: Consts.APPLICATION_JSON,
      "Content-Type": Consts.APPLICATION_JSON,
      Authorization: bearer,
    });
  };

  const saveDbDrug = async (bearer, drug, patientId) => {
    const transformed = transformNewDrugToSend(drug);

    if (transformed && patientId > 0 ) {
      return await request(
        `${_apiBase}prescription/create/${patientId}`,
        Consts.POST,
        transformed,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  /*const updateDbDrug = async (bearer, drug, patientId) => {
    if (drug !== null && patientId > 0) {
      return await request(
        `${_apiBase}prescription/update/${patientId}`,
        Consts.POST,
        transformDrugToUpdate(drug),
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };*/

  const getDbGenes = async (bearer, patientId) => {
    if (patientId > 0) {
      const res = await request(
        `${_apiBase}gene_test/gene_list/${patientId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return transformReceivedGenes(res);
    }

    return null;
  };

  const saveDbGeneTest = async (bearer, pgx, patientId) => {
    if (pgx !== null && patientId > 0) {
      return await request(
        `${_apiBase}gene_test/create/${patientId}`,
        Consts.POST,
        transformGeneTestToSend(pgx),
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const deleteDbGene = async (bearer, geneId, patientId) => {
    if (geneId > 0) {
      return await request(
        `${_apiBase}gene_test/delete/gene/${geneId}/${patientId}`,
        Consts.DELETE,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const saveDbDrugsQr = async (bearer, qrFormData, patientId) => {
    if (qrFormData !== null && patientId > 0) {
      return await request(
        `${_apiBase}prescription/upload/qr-code/${patientId}`,
        Consts.POST,
        qrFormData,
        { Authorization: bearer },
      );
    }

    return null;
  };

  const saveDbSymptomPictures = async (bearer, symptomFormData, symptomId) => {
    if (symptomFormData !== null) {
      return await request(
        `${_apiBase}symptom/upload/pictures/${symptomId}`,
        Consts.POST,
        symptomFormData,
        {
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const saveDbSymptom = async (bearer, symptoms, patientId) => {
    if (symptoms !== null && patientId > 0) {
      const transformedSymptoms = transformNewSymptomsToSend(symptoms, patientId);

      const response = await request(
        `${_apiBase}symptom/create`,
        Consts.POST,
        transformedSymptoms,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );

      return transformNewReceivedSymptoms(transformedSymptoms, response?.id);
    }

    return null;
  };

  const updateDbSymptom = async (bearer, symptom, patientId) => {
    if (symptom !== null && patientId > 0) {
      const transformedSymptom = transformUpdatedSymptomToSend(symptom, patientId);

      return await request(
        `${_apiBase}symptom/update/${patientId}`,
        Consts.PUT,
        transformedSymptom,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const getDiseasesJson = async (bearer) => {
    return await request(`${_apiBase}diseases`, Consts.GET, null, {
      Accept: Consts.APPLICATION_JSON,
      "Content-Type": Consts.APPLICATION_JSON,
      Authorization: bearer,
    });
  };

  const getDbDiseases = async (bearer, patientId) => {
    if (patientId > 0) {
      return await request(
        `${_apiBase}disease/list/${patientId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );
    }

    return null;
  };

  const getDbSymptoms = async (bearer, patientId) => {
    if (patientId > 0) {
      const result = await request(
        `${_apiBase}symptom/list/${patientId}`,
        Consts.GET,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );

      return transformReceivedSymptoms(result);
    }

    return null;
  };

  const deleteDbSymptom = async (bearer, symptomId) => {
    if (symptomId > 0) {
      return await request(
        `${_apiBase}symptom/delete/${symptomId}`,
        Consts.DELETE,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const getDbSymptomImage = async (bearer, imageId) => {
    if (imageId > 0) {
      return await request(
        `${_apiBase}symptom/picture/${imageId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
        true,
      );
    }

    return null;
  };

  const getDosageRecommendationReport = async (bearer, prescriptionId) => {
    if (prescriptionId > 0) {
      return await request(
        `${_apiBase}precisionDosing/report/${prescriptionId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );
    }

    return null;
  };

  const getDosageRecommendationReportFile = async (bearer, drugId) => {
    if (drugId > 0) {
      return await request(
        `${_apiBase}precisionDosing/report/file/${drugId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
        true,
      );
    }

    return null;
  };

  const getHisPdmReportFile = async (bearer, pdmId) => {
    if (pdmId > 0) {
      return await request(
        `${_apiBase}mmcSafetyAnnotator/pdm/file/${pdmId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
        true,
      );
    }

    return null;
  };

  const deleteDbSymptomPicture = async (bearer, imageId) => {
    if (imageId > 0) {
      return await request(
        `${_apiBase}symptom/picture/delete/${imageId}`,
        Consts.DELETE,
        null,
        {
          Accept: Consts.APPLICATION_JSON,
          "Content-Type": Consts.APPLICATION_JSON,
          Authorization: bearer,
        },
      );
    }

    return null;
  };

  const getDbSymptomImages = async (bearer, symptomId) => {
    if (symptomId > 0) {
      return await request(
        `${_apiBase}symptom/pictures/${symptomId}`,
        Consts.GET,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
        true,
      );
    }

    return null;
  };

  const requestDbPatientDataDelete = async (bearer, userId) => {
    if (userId) {
      const result = await request(
        `${_apiBase}user/delete/request/${userId}`,
        Consts.PUT,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return result && result > -1;
    }

    return false;
  };

  const cancelDbPatientDataDelete = async (bearer, userId) => {
    if (userId) {
      const result = await request(
        `${_apiBase}user/delete/cancel/${userId}`,
        Consts.PUT,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return result && result > -1;
    }

    return false;
  };

  const confirmDbPatientDataDelete = async (bearer, patientId) => {
    if (patientId) {
      const result = await request(
        `${_apiBase}user/delete/confirm/${patientId}`,
        Consts.PUT,
        null,
        { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer },
      );

      return result && result > -1;
    }

    return false;
  };

  const sendFeedback = async (bearer, feedback) => {
    const result = await request(
      `${_apiBase}user/feedback/create`,
      Consts.POST,
      feedback,
      { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer });

    return result && result?.id && result?.id?.id > -1;
  };

  const sendHealthLordRequest = async (bearer, body) => {
    return await request(
      `${_apiBase}mmcSafetyAnnotator/order/place`,
      Consts.POST,
      body,
      { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer });

  };

  const getHealthLordResults = async (bearer, orderId) => {
    return await request(
      `${_apiBase}mmcSafetyAnnotator/order/${orderId}`,
      Consts.GET,
      null,
      { "Content-Type": Consts.APPLICATION_JSON, Authorization: bearer });
  };

  const sendTestPdmPdf = async (bearer, testPdfFormData, orderId) => {
    if (testPdfFormData !== null && orderId !== null && orderId !== undefined && orderId !== "") {
      return await request(`${_apiBase}precisionDosing/order/finish/${orderId}`,  Consts.POST, testPdfFormData,  {
        Authorization: bearer
      });
    }

    return null;
  };

  return {
    loading,
    error,
    clearError,
    login,
    getDbUser,
    getDbLabTests,
    getDbMeasurements,
    getDbWeight,
    getDbHeight,
    getDbDrugs,
    getDbGenes,
    getDbSymptoms,
    getDrugProductsJson,
    getDrugSubstancesJson,
    getDrugByProductNameJson,
    getDrugBySubstancesJson,
    getDbDiseases,
    getDiseasesJson,
    getDpwgJson,
    getPromsJson,
    deleteDbLabTest,
    deleteDbDrug,
    deleteDbGene,
    deleteDbMeasurement,
    deleteDbDisease,
    deleteDbSymptom,
    saveDbLabTest,
    saveDbDisease,
    saveDbMeasurement,
    saveDbDrug,
    saveDbGeneTest,
    saveDbSymptom,
    saveDbDrugsQr,
    updateDbUser,
    //updateDbDrug,
    updateDbSymptom,
    saveDbSymptomPictures,
    getDbSymptomImage,
    getDosageRecommendationReport,
    getDosageRecommendationReportFile,
    deleteDbSymptomPicture,
    getDbSymptomImages,
    requestDbPatientDataDelete,
    cancelDbPatientDataDelete,
    confirmDbPatientDataDelete,
    sendFeedback,
    sendHealthLordRequest,
    getHealthLordResults,
    sendTestPdmPdf,
    getHisPdmReportFile
  };
};

export default useMmcService;
